import { useState } from 'react';
import { api } from '../lib/api.js';
import { useApi, useAction } from '../lib/hooks.js';
import { Loading, ErrorNote } from '../components/ui.jsx';

/**
 * The screen somebody sees when they have an account and no pantry.
 *
 * That state exists on purpose. Signing in used to create a household as a side
 * effect, which meant the commonest way to arrive here -- being invited -- built
 * a pantry and immediately abandoned it. Authenticating and owning a household
 * are different acts, so they are two steps now, and this is the second.
 *
 * Joining is offered FIRST when there is something to join. Somebody who has
 * been invited did not come here to start their own, and putting the create
 * button on top would invite exactly the mistake this change exists to remove.
 */
export default function FirstRun({ onReady }) {
  const pending = useApi('/household/pending');
  const invites = pending.data?.invites ?? [];

  if (pending.loading && !pending.data) return <Loading rows={3} />;

  return (
    <div className="stack-lg">
      <section>
        <div className="section-title"><h2>One more step</h2></div>

        {invites.length > 0 && <Invited invites={invites} onJoined={onReady} />}

        <JoinByCode highlighted={invites.length === 0} onJoined={onReady} />

        <StartOne onCreated={onReady} />
      </section>
    </div>
  );
}

/**
 * An invitation was addressed to the email they signed in with.
 *
 * Offered, never taken automatically. Being invited is not the same as
 * agreeing, and an account that silently landed in somebody else's household on
 * its first sign-in would be a bewildering way to begin.
 */
function Invited({ invites, onJoined }) {
  const { busy, error, run } = useAction();

  return (
    <div className="card stack" style={{ marginBottom: 14 }}>
      <ErrorNote error={error} />
      {invites.map((invite) => (
        <div key={invite.id} className="stack">
          <p>
            <strong>{invite.household_name}</strong> invited you.
          </p>
          <button
            type="button"
            className="primary full"
            disabled={busy}
            onClick={() => run(async () => {
              await api.post('/household/invites/accept', { code: invite.code });
              onJoined();
            })}
          >
            Join {invite.household_name}
          </button>
        </div>
      ))}
    </div>
  );
}

/** For somebody who was read the code rather than sent the link. */
function JoinByCode({ highlighted, onJoined }) {
  const [code, setCode] = useState('');
  const [open, setOpen] = useState(highlighted);
  const { busy, error, run } = useAction();

  if (!open) {
    return (
      <div className="card stack" style={{ marginBottom: 14 }}>
        <button type="button" className="full" onClick={() => setOpen(true)}>
          I have an invitation code
        </button>
      </div>
    );
  }

  return (
    <div className="card stack" style={{ marginBottom: 14 }}>
      <span className="small muted">Joining someone else&rsquo;s pantry</span>
      <ErrorNote error={error} />
      <label className="small muted" htmlFor="join-code">Invitation code</label>
      <input
        id="join-code"
        className="mono"
        value={code}
        placeholder="ABCD-2345"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck="false"
        onChange={(e) => setCode(e.target.value)}
      />
      <button
        type="button"
        className="primary full"
        disabled={busy || code.trim().length < 6}
        onClick={() => run(async () => {
          await api.post('/household/invites/accept', { code: code.trim() });
          onJoined();
        })}
      >
        Join
      </button>
    </div>
  );
}

/** The other half: no invitation, so this is their own pantry. */
function StartOne({ onCreated }) {
  const [name, setName] = useState('');
  const { busy, error, run } = useAction();

  return (
    <div className="card stack">
      <span className="small muted">Starting your own</span>
      <ErrorNote error={error} />
      <label className="small muted" htmlFor="household-name">
        What should it be called? (optional)
      </label>
      <input
        id="household-name"
        value={name}
        maxLength={80}
        placeholder="My Household"
        onChange={(e) => setName(e.target.value)}
      />
      <button
        type="button"
        className="full"
        disabled={busy}
        onClick={() => run(async () => {
          await api.post('/household', { name: name.trim() || undefined });
          onCreated();
        })}
      >
        Start a pantry
      </button>
    </div>
  );
}
